// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the canvas stage. The world scrolls right-to-left past a mote fixed near the
// left; a luminous cyan ribbon (a soft band with a bright core = the tolerance you must
// stay within) weaves across it. Steps draws held platforms as bright bars with dim
// connecting ramps; Thread draws coral walls with a green gap to thread. The mote glows
// and sparks when it's on the ribbon and dims when it drifts, leaving a fading trail.
// Draws purely from GameState; owns no rules. Guards against 0-size measurements.

import { PALETTE } from './palette';
import { ribbonAt, activeAt, type GameEvent, type GameState } from './game';

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

const MOTE_X_FRAC = 0.28; // the mote sits here across the width
const FUTURE_MS = 2600; // how far ahead the ribbon is shown
const PAST_MS = 900; // how far the trail streams behind
const TOP_FRAC = 0.12;
const BOT_FRAC = 0.88;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
}

export class Board {
  private ctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private particles: Particle[] = [];
  private trail: Array<{ t: number; h: number }> = [];
  private shake = 0;
  private flash = 0;
  private flashColor = '#ffffff';
  private glow = 0; // brightens the mote briefly on a good event

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = rect.width || this.canvas.clientWidth;
    const h = rect.height || this.canvas.clientHeight;
    if (w < 2 || h < 2) return; // ignore transient 0-size (backgrounded tab / mid-layout)
    this.dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    this.w = w;
    this.h = h;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
  }

  private moteX(): number {
    return this.w * MOTE_X_FRAC;
  }
  private pxPerMs(): number {
    return (this.w - this.moteX()) / FUTURE_MS;
  }
  private xOf(t: number, clock: number): number {
    return this.moteX() + (t - clock) * this.pxPerMs();
  }
  private yOf(hVal: number): number {
    const top = this.h * TOP_FRAC;
    const bot = this.h * BOT_FRAC;
    return top + (1 - clamp(hVal, 0, 1)) * (bot - top);
  }

  onEvents(events: GameEvent[], reducedMotion: boolean, state: GameState): void {
    const cx = this.moteX();
    const cy = this.yOf(state.height);
    for (const e of events) {
      if (e.t === 'combo') {
        this.burst(cx, cy, 10 + e.mult * 2, PALETTE.spark, 3.5);
        this.glow = 1;
      } else if (e.t === 'enter') {
        this.glow = Math.max(this.glow, 0.6);
      } else if (e.t === 'gate') {
        if (e.passed) {
          this.burst(cx, cy, 14, PALETTE.gap, 4);
          this.flash = Math.max(this.flash, 0.12);
          this.flashColor = PALETTE.gap;
        }
      } else if (e.t === 'note') {
        if (e.hit) this.burst(cx, cy, 12, PALETTE.spark, 3.5);
      } else if (e.t === 'die') {
        this.burst(cx, cy, 40, PALETTE.wall, 8);
        if (!reducedMotion) this.shake = Math.max(this.shake, 18);
        this.flash = Math.max(this.flash, 0.5);
        this.flashColor = PALETTE.wall;
      }
    }
  }

  private burst(x: number, y: number, n: number, color: string, speed: number): void {
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.5;
      const sp = speed * (0.4 + Math.random());
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 1,
        max: 0.4 + Math.random() * 0.5,
        size: 1.5 + Math.random() * 2.5,
        color,
      });
    }
  }

  frame(s: GameState, opts: { dtMs: number; reducedMotion: boolean }): void {
    const { dtMs, reducedMotion } = opts;
    const ctx = this.ctx;
    if (this.w < 2 || this.h < 2) this.resize();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);

    // Stage + vertical vignette.
    ctx.fillStyle = PALETTE.stage;
    ctx.fillRect(0, 0, this.w, this.h);
    const vg = ctx.createLinearGradient(0, 0, 0, this.h);
    vg.addColorStop(0, PALETTE.vignette);
    vg.addColorStop(0.5, 'rgba(0,0,0,0)');
    vg.addColorStop(1, PALETTE.vignette);
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, this.w, this.h);

    let ox = 0;
    let oy = 0;
    if (this.shake > 0.2 && !reducedMotion) {
      ox = (Math.random() - 0.5) * this.shake;
      oy = (Math.random() - 0.5) * this.shake;
      this.shake *= Math.exp(-dtMs / 90);
    } else {
      this.shake = 0;
    }
    ctx.save();
    ctx.translate(ox, oy);

    const clock = s.clock;
    const tol = s.mode.tol;
    const bandPx = tol * (this.h * (BOT_FRAC - TOP_FRAC));

    this.drawRibbon(s, clock, bandPx);
    if (s.mode.kind === 'thread') this.drawWalls(s, clock);

    // trail: record and draw recent mote positions, projected to current scroll.
    if (!s.over) {
      this.trail.push({ t: clock, h: s.height });
      while (this.trail.length && this.trail[0].t < clock - PAST_MS) this.trail.shift();
    }
    if (this.trail.length > 1) {
      ctx.lineCap = 'round';
      for (let i = 1; i < this.trail.length; i++) {
        const a = this.trail[i - 1];
        const b = this.trail[i];
        const age = (clock - b.t) / PAST_MS;
        ctx.strokeStyle = PALETTE.moteMid;
        ctx.globalAlpha = clamp(0.5 * (1 - age), 0, 0.5);
        ctx.lineWidth = 3 * (1 - age) + 1;
        ctx.beginPath();
        ctx.moveTo(this.xOf(a.t, clock), this.yOf(a.h));
        ctx.lineTo(this.xOf(b.t, clock), this.yOf(b.h));
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // the mote.
    this.glow = Math.max(0, this.glow - dtMs / 400);
    const mx = this.moteX();
    const my = this.yOf(s.height);
    const on = s.onLine;
    const base = on ? 12 : 9;
    const R = base + (on ? this.glow * 4 : 0);

    const glowR = R * (2.4 + this.glow);
    const gr = ctx.createRadialGradient(mx, my, R * 0.2, mx, my, glowR);
    gr.addColorStop(0, on ? PALETTE.moteMid : PALETTE.moteEdge);
    gr.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalAlpha = on ? 0.55 : 0.3;
    ctx.fillStyle = gr;
    ctx.beginPath();
    ctx.arc(mx, my, glowR, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    const body = ctx.createRadialGradient(mx - R * 0.34, my - R * 0.36, R * 0.1, mx, my, R);
    body.addColorStop(0, PALETTE.moteCore);
    body.addColorStop(0.55, PALETTE.moteMid);
    body.addColorStop(1, PALETTE.moteEdge);
    ctx.fillStyle = body;
    ctx.globalAlpha = on ? 1 : 0.75;
    ctx.beginPath();
    ctx.arc(mx, my, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = PALETTE.sheen;
    ctx.globalAlpha = on ? 0.85 : 0.5;
    ctx.beginPath();
    ctx.arc(mx - R * 0.34, my - R * 0.36, R * 0.24, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    // particles.
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const p of this.particles) {
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.96;
      p.vy *= 0.96;
      p.life -= dtMs / 1000 / p.max;
      ctx.globalAlpha = clamp(p.life, 0, 1);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // flash.
    if (this.flash > 0.01) {
      ctx.globalAlpha = this.flash;
      ctx.fillStyle = this.flashColor;
      ctx.fillRect(0, 0, this.w, this.h);
      ctx.globalAlpha = 1;
      this.flash *= Math.exp(-dtMs / 120);
    } else {
      this.flash = 0;
    }
  }

  private drawRibbon(s: GameState, clock: number, bandPx: number): void {
    const ctx = this.ctx;
    const t0 = clock - PAST_MS;
    const t1 = clock + FUTURE_MS;
    const stepMs = 26;
    const pts: Array<{ x: number; y: number; active: boolean }> = [];
    for (let t = t0; t <= t1; t += stepMs) {
      pts.push({ x: this.xOf(t, clock), y: this.yOf(ribbonAt(s.course, t)), active: activeAt(s.course, t) });
    }

    // soft tolerance band (the ribbon's forgiving width).
    ctx.strokeStyle = PALETTE.ribbon;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.16;
    ctx.lineWidth = Math.max(4, bandPx * 2);
    this.strokePath(pts, true);
    ctx.globalAlpha = 1;

    // bright core — dim where scoring is inactive (a Steps rest ramp).
    for (let seg = 0; seg < 2; seg++) {
      const wantActive = seg === 0;
      ctx.beginPath();
      let drawing = false;
      for (const p of pts) {
        if (p.active === wantActive) {
          if (!drawing) {
            ctx.moveTo(p.x, p.y);
            drawing = true;
          } else ctx.lineTo(p.x, p.y);
        } else {
          drawing = false;
        }
      }
      ctx.strokeStyle = PALETTE.ribbon;
      ctx.globalAlpha = wantActive ? 0.95 : 0.35;
      ctx.lineWidth = wantActive ? 3.5 : 2;
      if (!wantActive) ctx.setLineDash([6, 7]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.globalAlpha = 1;
  }

  private strokePath(pts: Array<{ x: number; y: number }>, all = false): void {
    const ctx = this.ctx;
    ctx.beginPath();
    for (let i = 0; i < pts.length; i++) {
      if (i === 0) ctx.moveTo(pts[i].x, pts[i].y);
      else ctx.lineTo(pts[i].x, pts[i].y);
    }
    if (all) ctx.stroke();
  }

  private drawWalls(s: GameState, clock: number): void {
    const ctx = this.ctx;
    const top = this.h * (TOP_FRAC - 0.06);
    const bot = this.h * (BOT_FRAC + 0.06);
    for (const g of s.course.gates) {
      const x = this.xOf(g.t, clock);
      if (x < -20 || x > this.w + 20) continue;
      const gy0 = this.yOf(g.h + g.gap);
      const gy1 = this.yOf(g.h - g.gap);
      ctx.fillStyle = PALETTE.wall;
      ctx.globalAlpha = 0.9;
      // wall above the gap and below the gap
      ctx.fillRect(x - 3, top, 6, gy0 - top);
      ctx.fillRect(x - 3, gy1, 6, bot - gy1);
      // the green gap markers (the passage)
      ctx.fillStyle = PALETTE.gap;
      ctx.globalAlpha = 0.95;
      ctx.fillRect(x - 4, gy0 - 3, 8, 3);
      ctx.fillRect(x - 4, gy1, 8, 3);
      ctx.globalAlpha = 1;
    }
  }

  destroy(): void {
    this.particles = [];
    this.trail = [];
  }
}
